import dynamic from 'next/dynamic';

import { Button, Text } from '@/design-system/components/ui';

import { EventTriggerType } from '../../domain';
import { UserIdentifierProperty } from '../../types';
import { TriggerFormSkeleton } from '../create-trigger-forms/TriggerFormSkeleton';
import { SelectTriggerOption } from '../TriggerTypeSelectorModal';
import { liquidityV3Config } from './triggerConfigs';

const CreateTokenHoldersTrigger = dynamic(
  () =>
    import('../create-trigger-forms/token-holders/create-token-holders-trigger').then(
      (mod) => mod.CreateTokenHoldersTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateOffChainTrigger = dynamic(
  () => import('../create-trigger-forms/off-chain/CreateOffChainTrigger').then((mod) => mod.CreateOffChainTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateGithubTrigger = dynamic(
  () => import('../create-trigger-forms/github/CreateGithubTrigger').then((mod) => mod.CreateGithubTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateDiscordTrigger = dynamic(
  () => import('../create-trigger-forms/discord/CreateDiscordTrigger').then((mod) => mod.CreateDiscordTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateSuiTokenHoldersTrigger = dynamic(
  () =>
    import('../create-trigger-forms/sui-token-holders/create-sui-token-holders-trigger').then(
      (mod) => mod.CreateSuiTokenHoldersTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateSolanaTokenHoldersTrigger = dynamic(
  () =>
    import('../create-trigger-forms/solana-token-holders/create-solana-token-holders-trigger').then(
      (mod) => mod.CreateSolanaTokenHoldersTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateFogoTokenHoldersTrigger = dynamic(
  () =>
    import('../create-trigger-forms/fogo-token-holders/create-fogo-token-holders-trigger').then(
      (mod) => mod.CreateFogoTokenHoldersTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateStellarTokenHoldersTrigger = dynamic(
  () =>
    import('../create-trigger-forms/stellar-token-holders/create-stellar-token-holders-trigger').then(
      (mod) => mod.CreateStellarTokenHoldersTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateOnChainTriggerStepper = dynamic(
  () =>
    import('../create-trigger-forms/on-chain/CreateOnChainTriggerStepper').then(
      (mod) => mod.CreateOnChainTriggerStepper,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateLiquidityV3Trigger = dynamic(
  () =>
    import('../create-trigger-forms/liquidity-v3/CreateLiquidityV3Trigger').then((mod) => mod.CreateLiquidityV3Trigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateLiquidityV4Trigger = dynamic(
  () =>
    import('../create-trigger-forms/liquidity-v4/CreateLiquidityV4Trigger').then((mod) => mod.CreateLiquidityV4Trigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateV2LiquidityTrigger = dynamic(
  () =>
    import('../create-trigger-forms/liquidity/CreateV2LiquidityTrigger').then((mod) => mod.CreateV2LiquidityTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateBalancerTrigger = dynamic(
  () => import('../create-trigger-forms/balancer/create-balancer-trigger').then((mod) => mod.CreateBalancerTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateValiantLiquidityTrigger = dynamic(
  () =>
    import('../create-trigger-forms/valiant-liquidity/create-valiant-liquidity-trigger').then(
      (mod) => mod.CreateValiantLiquidityTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateLynxTradeTrigger = dynamic(
  () => import('../create-trigger-forms/lynx-trade/CreateLynxTradeTrigger').then((mod) => mod.CreateLynxTradeTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateNadoTradeTrigger = dynamic(
  () => import('../create-trigger-forms/nado/CreateNadoTradeTrigger').then((mod) => mod.CreateNadoTradeTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateHibachiTradingTrigger = dynamic(
  () =>
    import('../create-trigger-forms/hibachi-trading/create-hibachi-trading-trigger').then(
      (mod) => mod.CreateHibachiTradingTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateHibachiAffiliatesTrigger = dynamic(
  () =>
    import('../create-trigger-forms/hibachi-affiliates/create-hibachi-affiliates-trigger').then(
      (mod) => mod.CreateHibachiAffiliatesTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateHyperliquidTradeTrigger = dynamic(
  () =>
    import('../create-trigger-forms/hyperliquid-trade/create-hyperliquid-trade-trigger').then(
      (mod) => mod.CreateHyperliquidTradeTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateAmbientTradeTrigger = dynamic(
  () =>
    import('../create-trigger-forms/ambient-trade/create-ambient-trade-trigger').then(
      (mod) => mod.CreateAmbientTradeTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateOrderlyBuilderTradesTrigger = dynamic(
  () =>
    import('../create-trigger-forms/orderly-builder-trades/create-orderly-builder-trades-trigger').then(
      (mod) => mod.CreateOrderlyBuilderTradesTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateValiantTradeTrigger = dynamic(
  () =>
    import('../create-trigger-forms/valiant-trade/create-valiant-trade-trigger').then(
      (mod) => mod.CreateValiantTradeTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateMorphoTrigger = dynamic(
  () => import('../create-trigger-forms/morpho/CreateMorphoTrigger').then((mod) => mod.CreateMorphoTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateMoneyMarketTrigger = dynamic(
  () =>
    import('../create-trigger-forms/money-market/create-money-market-trigger').then(
      (mod) => mod.CreateMoneyMarketTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateMorphoVaultsTrigger = dynamic(
  () =>
    import('../create-trigger-forms/morpho-vaults/create-morpho-vaults-trigger').then(
      (mod) => mod.CreateMorphoVaultsTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateEulerV2LoopingTrigger = dynamic(
  () =>
    import('../create-trigger-forms/euler-v2-looping/create-euler-v2-looping-trigger').then(
      (mod) => mod.CreateEulerV2LoopingTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateEulerVaultsTrigger = dynamic(
  () =>
    import('../create-trigger-forms/euler-vaults/create-euler-vaults-trigger').then(
      (mod) => mod.CreateEulerVaultsTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateMorphoBorrowerStrategyTrigger = dynamic(
  () =>
    import('../create-trigger-forms/morpho-borrower-strategy/create-morpho-borrower-strategy-trigger').then(
      (mod) => mod.CreateMorphoBorrowerStrategyTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateHypurrNetPoolPositionsTrigger = dynamic(
  () =>
    import('../create-trigger-forms/hypurr-net-pool-positions/create-hypurr-net-pool-positions-trigger').then(
      (mod) => mod.CreateHypurrNetPoolPositionsTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateHypurrEulerNetPositionsTrigger = dynamic(
  () =>
    import('../create-trigger-forms/hypurr-euler-net-positions/create-hypurr-euler-net-positions-trigger').then(
      (mod) => mod.CreateHypurrEulerNetPositionsTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateAquariusTrigger = dynamic(
  () => import('../create-trigger-forms/aquarius/create-aquarius-trigger').then((mod) => mod.CreateAquariusTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateAquariusSwapsTrigger = dynamic(
  () =>
    import('../create-trigger-forms/aquarius-swaps/create-aquarius-swaps-trigger').then(
      (mod) => mod.CreateAquariusSwapsTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateBlendTrigger = dynamic(
  () => import('../create-trigger-forms/blend/create-blend-trigger').then((mod) => mod.CreateBlendTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreatePyronTrigger = dynamic(
  () => import('../create-trigger-forms/pyron/create-pyron-trigger').then((mod) => mod.CreatePyronTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateExponentTrigger = dynamic(
  () => import('../create-trigger-forms/exponent/create-exponent-trigger').then((mod) => mod.CreateExponentTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateFogoIgnitionLockedTrigger = dynamic(
  () =>
    import('../create-trigger-forms/fogo-ignition-locked/create-fogo-ignition-locked-trigger').then(
      (mod) => mod.CreateFogoIgnitionLockedTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateFogoLendTrigger = dynamic(
  () => import('../create-trigger-forms/fogolend/create-fogolend-trigger').then((mod) => mod.CreateFogoLendTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateFelixVanillaCollateralTrigger = dynamic(
  () =>
    import('../create-trigger-forms/felix-vanilla-collateral/CreateFelixVanillaCollateralTrigger').then(
      (mod) => mod.CreateFelixVanillaCollateralTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateFelixCdpCollateralTrigger = dynamic(
  () =>
    import('../create-trigger-forms/felix-cdp-collateral/CreateFelixCdpCollateralTrigger').then(
      (mod) => mod.CreateFelixCdpCollateralTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateGearboxTrigger = dynamic(
  () => import('../create-trigger-forms/gearbox/CreateGearboxTrigger').then((mod) => mod.CreateGearboxTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreatePolymarketTradeVolumeTrigger = dynamic(
  () =>
    import('../create-trigger-forms/polymarket-trade-volume/create-polymarket-trade-volume-trigger').then(
      (mod) => mod.CreatePolymarketTradeVolumeTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateHypurrCardTransactionsTrigger = dynamic(
  () =>
    import('../create-trigger-forms/hypurr-card-transactions/create-hypurr-card-transactions-trigger').then(
      (mod) => mod.CreateHypurrCardTransactionsTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreatePendleYTTrigger = dynamic(
  () => import('../create-trigger-forms/pendle-yt/CreatePendleYTTrigger').then((mod) => mod.CreatePendleYTTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreatePendleLPTrigger = dynamic(
  () => import('../create-trigger-forms/pendle-lp/CreatePendleLPTrigger').then((mod) => mod.CreatePendleLPTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateXFollowTrigger = dynamic(
  () => import('../create-trigger-forms/x-follow/CreateXFollowTrigger').then((mod) => mod.CreateXFollowTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateXPostTrigger = dynamic(
  () => import('../create-trigger-forms/x-post/CreateXPostTrigger').then((mod) => mod.CreateXPostTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateDiscordServerJoinTrigger = dynamic(
  () =>
    import('../create-trigger-forms/discord-server-join/CreateDiscordServerJoinTrigger').then(
      (mod) => mod.CreateDiscordServerJoinTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateDiscordRoleAssignedTrigger = dynamic(
  () =>
    import('../create-trigger-forms/discord-role-assigned/CreateDiscordRoleAssignedTrigger').then(
      (mod) => mod.CreateDiscordRoleAssignedTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateStripePaymentTrigger = dynamic(
  () =>
    import('../create-trigger-forms/stripe-payment/create-stripe-payment-trigger').then(
      (mod) => mod.CreateStripePaymentTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateXRepostTrigger = dynamic(
  () => import('../create-trigger-forms/x-repost/CreateXRepostTrigger').then((mod) => mod.CreateXRepostTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateXLikeTrigger = dynamic(
  () => import('../create-trigger-forms/x-like/CreateXLikeTrigger').then((mod) => mod.CreateXLikeTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateZealyQuestTrigger = dynamic(
  () => import('../create-trigger-forms/zealy/CreateZealyQuestTrigger').then((mod) => mod.CreateZealyQuestTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateGalxeQuestTrigger = dynamic(
  () => import('../create-trigger-forms/galxe/CreateGalxeQuestTrigger').then((mod) => mod.CreateGalxeQuestTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateDuneQueryTrigger = dynamic(
  () => import('../create-trigger-forms/dune/CreateDuneQueryTrigger').then((mod) => mod.CreateDuneQueryTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateSubgraphBalanceTrigger = dynamic(
  () =>
    import('../create-trigger-forms/subgraph-balance/create-subgraph-balance-trigger').then(
      (mod) => mod.CreateSubgraphBalanceTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreatePacificaBuilderTradesTrigger = dynamic(
  () =>
    import('../create-trigger-forms/pacifica-builder-trades/create-pacifica-builder-trades-trigger').then(
      (mod) => mod.CreatePacificaBuilderTradesTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateOdysseyBuilderTradesTrigger = dynamic(
  () =>
    import('../create-trigger-forms/odyssey-builder-trades/create-odyssey-builder-trades-trigger').then(
      (mod) => mod.CreateOdysseyBuilderTradesTrigger,
    ),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateSpectraYTTrigger = dynamic(
  () => import('../create-trigger-forms/spectra-yt/CreateSpectraYTTrigger').then((mod) => mod.CreateSpectraYTTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

const CreateSpectraLPTrigger = dynamic(
  () => import('../create-trigger-forms/spectra-lp/CreateSpectraLPTrigger').then((mod) => mod.CreateSpectraLPTrigger),
  { loading: () => <TriggerFormSkeleton /> },
);

interface Props {
  triggerType: EventTriggerType;
  triggerOption: SelectTriggerOption;
  userIdentifierProperty: UserIdentifierProperty;
  onClose: () => void;
  initialFormValues?: Record<string, unknown>;
  editingTriggerId?: string | null;
}

export const AddTriggerFormManager = ({
  triggerType,
  triggerOption,
  userIdentifierProperty,
  onClose,
  initialFormValues,
  editingTriggerId,
}: Props) => {
  switch (triggerType) {
    case EventTriggerType.OnChainTokenHolder:
      return (
        <CreateTokenHoldersTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.CustomEvent:
      return (
        <CreateOffChainTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.Github:
      return (
        <CreateGithubTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.Discord:
      return (
        <CreateDiscordTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.SuiTokenHolders:
      return (
        <CreateSuiTokenHoldersTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.SolanaTokenHolders:
      return (
        <CreateSolanaTokenHoldersTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.FogoTokenHolders:
      return (
        <CreateFogoTokenHoldersTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.StellarTokenHolders:
      return (
        <CreateStellarTokenHoldersTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.OnChain:
    case EventTriggerType.OnChainFunction:
      return (
        <CreateOnChainTriggerStepper
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.Uniswap:
    case EventTriggerType.HyperswapLiquidityV3:
    case EventTriggerType.HybraLiquidityV3:
    case EventTriggerType.PrjXLiquidityV3:
    case EventTriggerType.KittenSwapLiquidityV3:
    case EventTriggerType.UltrasolidLiquidityV3:
    case EventTriggerType.UpheavalLiquidityV3:
    case EventTriggerType.NestLiquidityV3: {
      const config = liquidityV3Config[triggerType];
      return (
        <CreateLiquidityV3Trigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          protocol={config.protocol}
          protocolName={config.protocolName}
          fallbackChainIds={config.fallbackChainIds}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );
    }

    case EventTriggerType.UniswapV4:
      return (
        <CreateLiquidityV4Trigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.OnChainV2Liquidity:
      return (
        <CreateV2LiquidityTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.BalancerPool:
      return (
        <CreateBalancerTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.ValiantLiquidity:
      return (
        <CreateValiantLiquidityTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    // Trading triggers
    case EventTriggerType.LynxTrade:
      return (
        <CreateLynxTradeTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.NadoTrade:
    case EventTriggerType.NadoPurpsTrade:
      return (
        <CreateNadoTradeTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.HibachiTrading:
      return (
        <CreateHibachiTradingTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.HibachiAffiliates:
      return (
        <CreateHibachiAffiliatesTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.HyperliquidTrade:
      return (
        <CreateHyperliquidTradeTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.AmbientTrade:
      return (
        <CreateAmbientTradeTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.OrderlyBuilderTrades:
      return (
        <CreateOrderlyBuilderTradesTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.ValiantTrade:
      return (
        <CreateValiantTradeTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    // DeFi / Money Market triggers
    case EventTriggerType.Morpho:
      return (
        <CreateMorphoTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.MorphoVaults:
      return (
        <CreateMorphoVaultsTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.EulerV2Looping:
      return (
        <CreateEulerV2LoopingTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.EulerVaults:
      return (
        <CreateEulerVaultsTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.SpectraYT:
      return (
        <CreateSpectraYTTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.SpectraLP:
      return (
        <CreateSpectraLPTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.CompoundV3:
      return (
        <CreateMoneyMarketTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.MorphoBorrowerStrategy:
      return (
        <CreateMorphoBorrowerStrategyTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.HypurrNetPoolPositions:
      return (
        <CreateHypurrNetPoolPositionsTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.HypurrEulerNetPositions:
      return (
        <CreateHypurrEulerNetPositionsTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.AquariusLiquidity:
      return (
        <CreateAquariusTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.AquariusSwaps:
      return (
        <CreateAquariusSwapsTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.BlendPositions:
      return (
        <CreateBlendTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.Pyron:
      return (
        <CreatePyronTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.Exponent:
      return (
        <CreateExponentTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.FogoLend:
      return (
        <CreateFogoLendTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.FogoIgnitionLocked:
      return (
        <CreateFogoIgnitionLockedTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.FelixVanillaCollateral:
      return (
        <CreateFelixVanillaCollateralTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.FelixCdpCollateral:
      return (
        <CreateFelixCdpCollateralTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.Gearbox:
      return (
        <CreateGearboxTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.PolymarketTradeVolume:
      return (
        <CreatePolymarketTradeVolumeTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.HypurrCardTransactions:
      return (
        <CreateHypurrCardTransactionsTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    // Pendle triggers
    case EventTriggerType.Pendle:
      return (
        <CreatePendleYTTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.PendleLP:
      return (
        <CreatePendleLPTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    // Social triggers
    case EventTriggerType.XFollowEvent:
      return (
        <CreateXFollowTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.XPostEvent:
      return (
        <CreateXPostTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.DiscordServerJoin:
      return (
        <CreateDiscordServerJoinTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.DiscordRoleAssigned:
      return (
        <CreateDiscordRoleAssignedTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.StripePayment:
      return (
        <CreateStripePaymentTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.XRepostEvent:
      return (
        <CreateXRepostTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.XLikeEvent:
      return (
        <CreateXLikeTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    // Quest triggers
    case EventTriggerType.Zealy:
      return (
        <CreateZealyQuestTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.Galxe:
      return (
        <CreateGalxeQuestTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    // Other triggers
    case EventTriggerType.Dune:
      return (
        <CreateDuneQueryTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.OnChainSubgraphEvent:
      return (
        <CreateSubgraphBalanceTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.PacificaBuilderTrades:
      return (
        <CreatePacificaBuilderTradesTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    case EventTriggerType.OdysseyBuilderTrades:
      return (
        <CreateOdysseyBuilderTradesTrigger
          triggerType={triggerType}
          triggerOption={triggerOption}
          userIdentifierProperty={userIdentifierProperty}
          handleClose={onClose}
          handleReturn={onClose}
          initialFormValues={initialFormValues}
          editingTriggerId={editingTriggerId}
        />
      );

    default:
      return (
        <div className="tw:flex tw:flex-col tw:gap-4">
          <Text variant="body-sm" color="muted">
            Coming soon: This trigger type is not yet supported in the new flow
          </Text>
          <Button onClick={onClose} variant="ghost">
            Back
          </Button>
        </div>
      );
  }
};
